/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Create models using dbt models and analyses", description = "Connect Hightouch to your dbt project's Git repository and build Hightouch models using existing dbt models and analyses.", lastUpdatedDate = new Date(1775001600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Create models using dbt models and analyses",
        description: "Connect Hightouch to your dbt project's Git repository and build Hightouch models using existing dbt models and analyses.",
        lastUpdatedDate: "Wed Apr 01 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Supported dbt versions",
          "depth": 0,
          "id": "supported-dbt-versions"
        }, {
          "value": "Connect to your Git repository",
          "depth": 0,
          "id": "connect-to-your-git-repository"
        }, {
          "value": "Connecting a source",
          "depth": 0,
          "id": "connecting-a-source"
        }, {
          "value": "Configure dbt model selector",
          "depth": 0,
          "id": "configure-dbt-model-selector"
        }, {
          "value": "Select a model",
          "depth": 0,
          "id": "select-a-model"
        }, {
          "value": "Model column descriptions",
          "depth": 1,
          "id": "model-column-descriptions"
        }, {
          "value": "Unsupported features",
          "depth": 0,
          "id": "unsupported-features"
        }, {
          "value": "dbt model advantages",
          "depth": 0,
          "id": "dbt-model-advantages"
        }, {
          "value": "dbt observability layer",
          "depth": 0,
          "id": "dbt-observability-layer"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Credential errors",
          "depth": 1,
          "id": "credential-errors"
        }, {
          "value": "Duplicated mapping key error",
          "depth": 1,
          "id": "duplicated-mapping-key-error"
        }, {
          "value": "The YAML property file is missing a version tag",
          "depth": 1,
          "id": "the-yaml-property-file-is-missing-a-version-tag"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      a: "a",
      img: "img",
      h2: "h2",
      strong: "strong",
      code: "code",
      em: "em",
      h3: "h3",
      ul: "ul",
      li: "li"
    }, _provideComponents(), props.components), {PylonLink, CommonErrorsPreface} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["Now you can sync models from ", _jsx(_components.a, {
            href: "/extensions/dbt-cloud",
            children: "dbt Cloud"
          }), "."]
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "This extension requires a paid Hightouch plan"
        })
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Hightouch can communicate with two distinct Git repository types. The dbt extension interacts with Git repositories that contain dbt projects. These are separate from the Git repository used for ", _jsx(_components.a, {
            href: "/extensions/git-sync",
            children: "Git Sync"
          }), " to version control your Hightouch settings."]
        })
      }), "\n", _jsxs(_components.p, {
        children: ["If you've invested in building models with ", _jsx(_components.a, {
          href: "https://www.getdbt.com/",
          children: "dbt"
        }), ", you likely want to use those models as the source of truth for your downstream data needs. At Hightouch, we use dbt to build most of our models for analytics and reporting."]
      }), "\n", _jsxs(_components.p, {
        children: ["Our dbt integration lets you define Hightouch models using your dbt models and ", _jsx(_components.a, {
          href: "https://docs.getdbt.com/docs/build/analyses",
          children: "analyses"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-models.png",
          alt: "dbt-based models in Hightouch"
        })
      }), "\n", _jsx(_components.h2, {
        id: "supported-dbt-versions",
        children: "Supported dbt versions"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch supports dbt Core 1.10 and newer. You can set the version on the dbt setup page. If you need support for a version not listed, ", _jsx(PylonLink, {
          linkText: "let us know"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "If you update your dbt version, return to Hightouch to update your dbt version selection. Once you've made the update, it may take up to 15 minutes for the changes to take effect."
        })
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-your-git-repository",
        children: "Connect to your Git repository"
      }), "\n", _jsxs(_components.p, {
        children: ["Navigate to the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/dbt-models/configuration?from=docs",
          children: "dbt extension configuration"
        }), " page and enter your Git credentials under ", _jsx(_components.strong, {
          children: "Credentials"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you're using GitHub, it's best to use ", _jsx(_components.a, {
          href: "https://github.com/apps/hightouch-connect/",
          children: "Hightouch's GitHub App"
        }), " for the most up-to-date feature support."]
      }), "\n", _jsxs(_components.p, {
        children: ["To set it up, choose ", _jsx(_components.strong, {
          children: "GitHub App"
        }), " as the ", _jsx(_components.strong, {
          children: "Git Service"
        }), ", and then select ", _jsx(_components.strong, {
          children: "Configure GitHub App"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-models-git-credential-selection.png",
          alt: "Hightouch GitHub app"
        })
      }), "\n", _jsx(_components.p, {
        children: "This prompts you to install the Hightouch Connect app in the relevant GitHub repository."
      }), "\n", _jsxs(_components.p, {
        children: ["For other Git services, you can choose to use either ", _jsx(_components.strong, {
          children: "SSH"
        }), " or ", _jsx(_components.strong, {
          children: "HTTP"
        }), " protocol. SSH protocol requires you provide an ", _jsx(_components.strong, {
          children: "SSH private key"
        }), " and HTTP requires a ", _jsx(_components.strong, {
          children: "username"
        }), " and ", _jsx(_components.strong, {
          children: "token"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-models-git-credentials-username-token.png",
          alt: "Hightouch GitHub app"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["See ", _jsx(_components.a, {
          href: "https://docs.github.com/en/github/authenticating-to-github/keeping-your-account-and-data-secure/creating-a-personal-access-token",
          children: "GitHub documentation"
        }), " or ", _jsx(_components.a, {
          href: "https://docs.gitlab.com/ee/user/profile/personal_access_tokens.html",
          children: "GitLab documentation"
        }), " for instructions on creating personal access tokens."]
      }), "\n", _jsx(_components.h2, {
        id: "connecting-a-source",
        children: "Connecting a source"
      }), "\n", _jsxs(_components.p, {
        children: ["You can use dbt models and analyses from your Git repository for any ", _jsx(_components.a, {
          href: "https://app.hightouch.com/sources?from=docs",
          children: "source"
        }), " in Hightouch. On the ", _jsx(_components.a, {
          href: "https://app.hightouch.com/extensions/dbt-models/configuration?from=docs",
          children: "extensions configuration page"
        }), ", there is a list of sources from your workspace within Hightouch that can be connected to dbt models. Choose one that you would like to configure and click \"Connect to dbt.\""]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-model-connect-source.png",
          alt: "Connect Source dbt"
        })
      }), "\n", _jsx(_components.h2, {
        id: "configure-dbt-model-selector",
        children: "Configure dbt model selector"
      }), "\n", _jsxs(Tip, {
        children: [_jsxs(_components.p, {
          children: ["When connecting dbt sources for the first time or making changes to your dbt model sync configuration, you usually only need to click ", _jsx(_components.strong, {
            children: "Save"
          }), " and not ", _jsx(_components.strong, {
            children: "Full Resync"
          }), " for Hightouch to create or update your configuration."]
        }), _jsx("br", {}), _jsxs(_components.p, {
          children: ["The ", _jsx(_components.strong, {
            children: "Full Resync"
          }), " action is only necessary when making significant changes in your dbt Git repository. It restarts the dbt compilation from a fresh state and can take a significant amount of time. After clicking this button, wait for the updated dbt models to compile before making any changes to your configuration."]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["The default schema is the schema where dbt ", _jsx(_components.a, {
          href: "https://docs.getdbt.com/docs/build/materializations",
          children: "materializes"
        }), " your tables to unless otherwise specified in project file. Some common examples are ", _jsx(_components.code, {
          children: "public"
        }), ", ", _jsx(_components.code, {
          children: "production"
        }), ", ", _jsx(_components.code, {
          children: "dbt_production"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you optionally specify ", _jsx(_components.a, {
          href: "https://docs.getdbt.com/reference/node-selection/syntax#examples",
          children: "dbt selectors"
        }), " like ", _jsx(_components.code, {
          children: "tag:hightouch"
        }), ". Otherwise, we use ", _jsx(_components.code, {
          children: "*"
        }), " and sync ", _jsx(_components.em, {
          children: "all"
        }), " dbt models to Hightouch."]
      }), "\n", _jsx(_components.p, {
        children: "You can also specify which version of dbt you want to use. By default, Hightouch uses the most recent version available in the drop-down menu."
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch's dbt integration automatically looks for your ", _jsx(_components.code, {
          children: "dbt_project.yml"
        }), " file in the repository directory. If your project file is named something else, for example, ", _jsx(_components.code, {
          children: "dbt_project.yaml"
        }), ", or you have multiple ", _jsx(_components.code, {
          children: "dbt_project.yml"
        }), " files, you can specify a relative path to your project file, for example, ", _jsx(_components.code, {
          children: "./hightouch/dbt/dbt_project.yaml"
        }), "."]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "After saving your settings, it may take a one to two minutes for Hightouch to sync your dbt models and for the sync status to turn green."
        })
      }), "\n", _jsx(_components.h2, {
        id: "select-a-model",
        children: "Select a model"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've configured the dbt model selector, you can use dbt models and analyses in the Hightouch ", _jsx(_components.a, {
          href: "/models/creating-models",
          children: "model creation process"
        }), ". Select the dbt model tab in the query explorer to view all your available dbt models and analyses."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-models-selector.png",
          alt: "dbt model selection"
        })
      }), "\n", _jsx(_components.p, {
        children: "The compiled SQL shown in the preview is what's ran against your source. We do not leverage the output table written by dbt."
      }), "\n", _jsx(_components.h3, {
        id: "model-column-descriptions",
        children: "Model column descriptions"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch pulls in column descriptions from dbt.\nYou can see them in the ", _jsx(_components.strong, {
          children: "Columns"
        }), " tab of a model's overview page."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "models/column-descriptions.png",
          alt: "Column descriptions in the Hightouch app"
        })
      }), "\n", _jsx(_components.h2, {
        id: "unsupported-features",
        children: "Unsupported features"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch doesn't support the following features:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "Private dependency packages"
        }), "\n", _jsx(_components.li, {
          children: "Pre- and post-hooks (limited support, this may work depending on the hook)"
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "https://docs.getdbt.com/docs/build/python-models",
            children: "Python models"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch compiles the project using ", _jsx(_components.code, {
          children: "dbt compile"
        }), " to resolve your data warehouse's schema and generate compiled SQL for preview purposes. When running the model in Hightouch, we query the materialized table directly."]
      }), "\n", _jsx(_components.h2, {
        id: "dbt-model-advantages",
        children: "dbt model advantages"
      }), "\n", _jsxs(_components.p, {
        children: ["When deciding between Hightouch's ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/models/table-selector",
          children: "table selector"
        }), " and our ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/extensions/dbt-models",
          children: "dbt model selector"
        }), ", the advantages of leveraging dbt models might not be immediately clear, since many dbt models are often materialized as tables and views in a ", _jsx(_components.a, {
          href: "https://hightouch.com/docs/getting-started/concepts#sources",
          children: "Source"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "However, there are a few key benefits of building Hightouch Models using the dbt model selector:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: "It provides a clean, information-rich modal with metadata about the underlying model, including its unique ID, description, and repository link."
        }), "\n", _jsxs(_components.li, {
          children: ["It allows you to automatically pull in ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/extensions/dbt-models#model-column-descriptions",
            children: "column descriptions"
          }), " into Hightouch for data governance/organization."]
        }), "\n", _jsxs(_components.li, {
          children: ["It allows you to leverage our ", _jsx(_components.a, {
            href: "https://hightouch.com/docs/extensions/dbt-observability",
            children: "dbt observability layer"
          }), ", which includes dbt exposures and CI checks."]
        }), "\n", _jsx(_components.li, {
          children: "Not all dbt resources materialize as tables/views in the warehouse. For example, dbt analyses will not form tables/views at all."
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "dbt-observability-layer",
        children: "dbt observability layer"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch lets you ", _jsx(_components.a, {
          href: "/extensions/dbt-observability#dbt-ci-checks",
          children: "validate dbt model changes"
        }), " as they relate to Hightouch syncs and observe ", _jsx(_components.a, {
          href: "/extensions/dbt-observability#dbt-exposure-sync",
          children: "how Hightouch syncs are using your dbt models"
        }), ". Check out our ", _jsx(_components.a, {
          href: "/extensions/dbt-observability",
          children: "dbt observability layer documentation"
        }), " for more information."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Hightouch compiles the project using ", _jsx(_components.a, {
            href: "https://docs.getdbt.com/reference/commands/compile",
            children: _jsx(_components.code, {
              children: "dbt compile"
            })
          }), " to resolve your data warehouse's schema and generate compiled SQL for your model. If you encounter error messages on your dbt extension configuration page, try running ", _jsx(_components.a, {
            href: "https://docs.getdbt.com/reference/commands/compile",
            children: _jsx(_components.code, {
              children: "dbt compile"
            })
          }), " locally on your dbt project to see if the errors still occur."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "credential-errors",
        children: "Credential errors"
      }), "\n", _jsxs(_components.p, {
        children: ["If your Git credentials are out of date, you won't be able to connect your dbt models and analyses.\nWe recommend using ", _jsx(_components.a, {
          href: "#connecting-to-your-git-repository",
          children: "Hightouch's GitHub app"
        }), " for your credentials if you're using GitHub."]
      }), "\n", _jsx(_components.h3, {
        id: "duplicated-mapping-key-error",
        children: "Duplicated mapping key error"
      }), "\n", _jsxs(_components.p, {
        children: ["You may receive a ", _jsx(_components.code, {
          children: "duplicated mapping key"
        }), " error under your dbt model sync status if there are duplicate keys in your ", _jsx(_components.code, {
          children: "dbt_project.yml"
        }), " file.\nTo resolve the error, you can either remove the duplicates or rename duplicated keys."]
      }), "\n", _jsx(_components.h3, {
        id: "the-yaml-property-file-is-missing-a-version-tag",
        children: "The YAML property file is missing a version tag"
      }), "\n", _jsxs(_components.p, {
        children: ["This dbt compilation error can occur if you use the same Git repository for ", _jsx(_components.a, {
          href: "/extensions/git-sync",
          children: "Git Sync"
        }), " and ", _jsx(_components.a, {
          href: "#configure-dbt-model-selector",
          children: "dbt models sync"
        }), ".\nTo solve this, make sure that you're using two different Git repositories for these extensions.\nOtherwise, if you would like to use the same repository, you can specify a custom path in the ", _jsx(_components.a, {
          href: "/extensions/git-sync#configure-git-sync",
          children: "Git Sync configuration"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "integrations/dbt-models-version-tag.png",
          alt: "the YAML property file is missing a version tag"
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
